import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import DeleteModal from "../../../components/DeleteModal";
import VehicleCategoriesHeader from "./_components/VehicleCategoriesHeader";
import VehicleCategoriesTable from "./_components/VehicleCategoriesTable";
import {
  useDeleteVehicleCategoryMutation,
  useGetVehicleCategories,
} from "../../../../api/fleet-management/vehicle-category/vehicle-category.hooks";
import {
  filterVehicleCategories,
  normalizeVehicleCategories,
} from "./utils/vehicle-category.utils";

const VehicleCategories = () => {
  const history = useHistory();
  const { data, isLoading, isError, error } = useGetVehicleCategories();
  const { mutate: deleteCategory, isLoading: isDeleting } = useDeleteVehicleCategoryMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = normalizeVehicleCategories(data);

  const filteredCategories = useMemo(() => {
    return filterVehicleCategories(categories, searchTerm);
  }, [categories, searchTerm]);

  const handleEdit = (categoryId) => {
    history.push(`/fleet-management/vehicle-categories/update/${categoryId}`);
  };

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setSelectedCategory(null);
  };

  const handleConfirmDelete = () => {
    if (!selectedCategory?._id) return;

    setFeedback({ type: "", message: "" });
    deleteCategory(selectedCategory._id, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Vehicle category deleted successfully." });
        closeDeleteModal();
      },
      onError: (deleteError) => {
        setFeedback({
          type: "danger",
          message: deleteError?.response?.data?.message || "Failed to delete vehicle category.",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load vehicle categories. {error?.response?.data?.message || "Please try again."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <VehicleCategoriesHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          total={filteredCategories.length}
          onAddCategory={() => history.push("/fleet-management/vehicle-categories/add")}
        />

        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {filteredCategories.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No vehicle categories found</p>
            </div>
          ) : (
            <VehicleCategoriesTable
              categories={filteredCategories}
              onEdit={handleEdit}
              onDelete={openDeleteModal}
              isDeleting={isDeleting}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Vehicle Category"
        message={`Are you sure you want to delete ${selectedCategory?.name || "this vehicle category"}? This action cannot be undone.`}
        confirmText="Delete Category"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default VehicleCategories;
